import { ViewTransition } from "react";
import { useNavigate, useParams } from "react-router";
import * as stylex from "@stylexjs/stylex";

import { BLOG_POSTS } from "../data/placeholder";

function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const selectedPost = BLOG_POSTS.find((p) => p.id === Number(id));

  if (!selectedPost) {
    return (
      <div {...stylex.props(styles.page)}>
        <div {...stylex.props(styles.container)}>
          <h1 {...stylex.props(styles.title)}>Post not found</h1>
          <button
            onClick={() => navigate("/blog")}
            {...stylex.props(styles.backButton)}
          >
            Back to Articles
          </button>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    navigate("/blog");
  };

  return (
    <div {...stylex.props(styles.page)}>
      <div {...stylex.props(styles.container)}>
        <ViewTransition name={`post-detail-${selectedPost.id}`}>
          <article {...stylex.props(styles.articleView)}>
            <button onClick={handleBack} {...stylex.props(styles.backButton)}>
              Back to Articles
            </button>

            <div {...stylex.props(styles.articleImageWrapper)}>
              <img
                src={selectedPost.image}
                alt={selectedPost.title}
                {...stylex.props(styles.articleImage)}
              />
            </div>

            <div {...stylex.props(styles.articleMeta)}>
              <span {...stylex.props(styles.categoryTag)}>
                {selectedPost.category}
              </span>
              <span {...stylex.props(styles.metaText)}>
                {selectedPost.date}
              </span>
              <span {...stylex.props(styles.metaDot)}>•</span>
              <span {...stylex.props(styles.metaText)}>
                {selectedPost.readTime}
              </span>
            </div>

            <h1 {...stylex.props(styles.articleTitle)}>{selectedPost.title}</h1>

            <div {...stylex.props(styles.articleContent)}>
              {selectedPost.content.split("\n").map((paragraph, index) =>
                paragraph.trim() ? (
                  <p key={index} {...stylex.props(styles.articleParagraph)}>
                    {paragraph.trim()}
                  </p>
                ) : null,
              )}
            </div>
          </article>
        </ViewTransition>
      </div>
    </div>
  );
}

export default BlogPost;

const fadeIn = stylex.keyframes({
  "0%": { opacity: 0, transform: "translateY(20px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const styles = stylex.create({
  page: {
    backgroundAttachment: "fixed",
    backgroundImage: "var(--bg-gradient)",
    minHeight: "100vh",
    width: "100%",
  },
  container: {
    gap: "2rem",
    marginInline: "auto",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    maxWidth: "900px",
    paddingBottom: "4rem",
    paddingLeft: {
      default: "2rem",
      "@media (max-width: 768px)": "1rem",
    },
    paddingRight: {
      default: "2rem",
      "@media (max-width: 768px)": "1rem",
    },
    paddingTop: "4rem",
    width: "100%",
  },
  articleContent: {
    gap: "1rem",
    display: "flex",
    flexDirection: "column",
  },
  articleImage: {
    objectFit: "cover",
    height: "100%",
    width: "100%",
  },
  articleImageWrapper: {
    borderRadius: "16px",
    overflow: "hidden",
    height: {
      default: "400px",
      "@media (max-width: 768px)": "250px",
    },
    marginBottom: "1.5rem",
    width: "100%",
  },
  articleMeta: {
    gap: "0.75rem",
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    marginBottom: "1rem",
  },
  articleParagraph: {
    color: "var(--text-secondary)",
    fontSize: "1.1rem",
    lineHeight: 1.8,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  articleTitle: {
    color: "var(--primary-text)",
    fontSize: {
      default: "2.5rem",
      "@media (max-width: 768px)": "1.75rem",
    },
    fontWeight: 800,
    letterSpacing: "-0.02em",
    lineHeight: 1.2,
    marginBottom: "2rem",
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  articleView: {
    animationDuration: "0.5s",
    animationFillMode: "both",
    animationName: fadeIn,
    animationTimingFunction: "ease-out",
  },
  backButton: {
    borderColor: {
      default: "var(--media-inner-border)",
      ":hover": "var(--primary-accent)",
    },
    borderRadius: "50px",
    borderStyle: "solid",
    borderWidth: "1px",
    transition: "all 0.3s ease",
    backgroundColor: {
      default: "var(--media-inner-border)",
      ":hover": "var(--primary-accent)",
    },
    boxShadow: {
      default: null,
      ":hover": "var(--shadow-neon)",
    },
    color: {
      default: "var(--primary-text)",
      ":hover": "#FFFFFF",
    },
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 600,
    transform: {
      default: null,
      ":hover": "translateX(-4px)",
    },
    marginBottom: "2rem",
    paddingBottom: "0.75rem",
    paddingLeft: "1.25rem",
    paddingRight: "1.25rem",
    paddingTop: "0.75rem",
  },
  categoryTag: {
    borderColor: "var(--primary-accent)",
    borderRadius: "50px",
    borderStyle: "solid",
    borderWidth: "1px",
    backgroundColor: "rgba(121, 40, 202, 0.1)",
    color: "var(--primary-accent)",
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    paddingBottom: "0.2rem",
    paddingLeft: "0.6rem",
    paddingRight: "0.6rem",
    paddingTop: "0.2rem",
  },
  metaDot: {
    color: "var(--text-secondary)",
    opacity: 0.5,
  },
  metaText: {
    color: "var(--text-secondary)",
    fontSize: "0.8rem",
  },
  title: {
    color: "var(--primary-text)",
  },
});
